/**
 * Single source of truth for the namespaced keys we write into
 * ContractCharacteristic when persisting an Immobilie contract.
 *
 * The shape on the wire is { nameBezeichnung, contractCharacteristicValue }.
 * All values are stored as strings (varchar 255). Booleans are 'true' / 'false'.
 */

export const CCK = {
  // Allgemeine Informationen
  inkassoart: "immo.inkassoart",
  rechnungsstellung: "immo.rechnungsstellung",

  // Vorvertrag
  vorvertragExists: "immo.vorvertrag.exists",
  vorvertragIsFremd: "immo.vorvertrag.is_fremdvertrag",
  vorvertragExistingId: "immo.vorvertrag.existing_contract_id",
  vorvertragGesellschaft: "immo.vorvertrag.gesellschaft",
  vorvertragNr: "immo.vorvertrag.vertragsnummer",
  vorvertragAlreadyCancelled: "immo.vorvertrag.already_cancelled",
  vorvertragKuendigung: "immo.vorvertrag.kuendigung_zum",
  vorvertragKuendigungsgrund: "immo.vorvertrag.kuendigungsgrund",
  vorvertragShouldCancel: "immo.vorvertrag.should_cancel",

  // Versicherer / Vertragsart / Tarifierung
  versicherer: "immo.versicherer.key",
  vertragsart: "immo.vertragsart",
  tarifierung: "immo.tarifierung",

  // Vereinbarung
  vereinbarungStatus: "immo.vereinbarung.status",
  vereinbarungDocId: "immo.vereinbarung.doc_id",

  // Risiko
  hauptrisikoId: "immo.hauptrisiko_id",
  temporaereRisikoIdsJson: "immo.temporaere_risiken_ids_json",
  temporaereAusschlussJson: "immo.temporaere_ausschluss_json",

  // UVV (neue Form)
  uvvVereinbartJa: "immo.uvv.vereinbart",         // "ja" | "nein"
  uvvVariante: "immo.uvv.variante",               // "courte"|"versicherer"|"gutachten"|"sonstiges"
  uvvSonstiges: "immo.uvv.sonstiges_text",

  // Vorvertrag-Gefahren (nur bei Fremdvertrag) — kompletter Tabellen-Stand als JSON
  vorvertragGefahrenJson: "immo.vorvertrag.gefahren_json",
  vorvertragMvHaftzeit: "immo.vorvertrag.mv_haftzeit",

  // Volldeckung & Prämienberechnung (Differenzdeckung-Felder)
  volldeckungAb: "immo.volldeckung.ab",
  praemieDerzeit: "immo.volldeckung.praemie_derzeit",
  praemieVolldeckung: "immo.volldeckung.praemie_volldeckung",
  praemieDifferenz: "immo.volldeckung.praemie_differenz",

  // Bedingungen & Dokumente
  bedingungenAllgemeinText: "immo.bedingungen.allgemein_text",
  bedingungenAllgemeinDocId: "immo.bedingungen.allgemein_doc_id",
  bedingungenBesondereText: "immo.bedingungen.besondere_text",
  bedingungenBesondereDocId: "immo.bedingungen.besondere_doc_id",

  // Versicherungssumme
  vs1914: "immo.vs.wert1914",
  vsBaupreisindex: "immo.vs.baupreisindex",
  vsBaupreisindexJahr: "immo.vs.baupreisindex_jahr",
  vsSumme1914: "immo.vs.summe1914_eur",
  vs1980: "immo.vs.wert1980",
  vsWertzuschlag: "immo.vs.wertzuschlag_pct",
  vsWertzuschlagJahr: "immo.vs.wertzuschlag_jahr",
  vsSumme1980: "immo.vs.summe1980_eur",
  vsAnteileJson: "immo.vs.anteile_json",

  // Unterversicherungsverzicht
  uvvVereinbart: "immo.uvv.vereinbart",
  uvvCourte: "immo.uvv.gem_courte",
  uvvVersicherer: "immo.uvv.gem_versicherer",
  uvvGutachten: "immo.uvv.gem_gutachten",
  uvvNicht: "immo.uvv.nicht_vereinbart",

  // Mietverlust — gilt für alle Gefahren, die in der Tabelle Mietverlust = ja haben
  mvGefahren: "immo.mietverlust.gefahren",      // Komma-Liste der Gefahren-IDs
  mvHaftzeit: "immo.mietverlust.haftzeit_monate",
  mvVsArt: "immo.mietverlust.vs_art",          // "20pct" | "brutto"
  mvVsBrutto: "immo.mietverlust.vs_brutto_eur",
  mvTemporaer: "immo.mietverlust.temporaer_vermietet",
  mvTemporaerWelche: "immo.mietverlust.temporaer_welche",
  mvAusschluss: "immo.mietverlust.ausschluss",  // "teil" | "komplett" | ""

  // Versicherte Gefahren — kompletter Tabellen-Stand als JSON (führende Quelle)
  gefahrenJson: "immo.gefahren_json",

  // Beträge je Gefahr zusätzlich als flache Maps (id → € als reine Ziffern)
  hePerGefahrJson: "immo.he.per_gefahr_json",
  jhePerGefahrJson: "immo.jhe.per_gefahr_json",
  sbPerGefahrJson: "immo.sb.per_gefahr_json",

  // Legacy: Verträge vor der Gefahren-Tabelle — nur noch lesend ausgewertet
  heScope: "immo.he.scope",
  heValue: "immo.he.value",
  jheScope: "immo.jhe.scope",
  jheValue: "immo.jhe.value",
  mvScope: "immo.mietverlust.scope",
  mvHaftzeitPerGefahr: "immo.mietverlust.haftzeit_per_gefahr_json",

  // Legacy Jahreshöchstentschädigung keys (kept for read-side compatibility)
  jheWE: "immo.jhe.we",
  jheIBS: "immo.jhe.ibs",
  jheFR: "immo.jhe.fr",
  jheUGWUG: "immo.jhe.ug_wug",

  // Antragsart
  antragsart: "immo.antragsart", // "neu" | "ersatz"
  vorvertragsnrGenerali: "immo.vorvertragsnr_generali",

  // Bedingungen (informational, stored for audit)
  bedingungenAllgemein: "immo.bedingungen.allgemein",
  bedingungenBesondere: "immo.bedingungen.besondere",

  // Photovoltaik
  pvVorhanden: "immo.photovoltaik.vorhanden",
  pvPolicennr: "immo.photovoltaik.policennr",

  // Differenzdeckung
  ddBeginn: "immo.differenzdeckung.beginn",
  ddVorvertragGesellschaft: "immo.differenzdeckung.vorvertrag_gesellschaft",
  ddVorvertragNr: "immo.differenzdeckung.vorvertrag_nr",
  ddVorvertragKuendigungZum: "immo.differenzdeckung.vorvertrag_kuendigung_zum",
  ddVolldeckungAb: "immo.differenzdeckung.volldeckung_ab",
  ddPraemieDerzeit: "immo.differenzdeckung.praemie_derzeit_eur",
  ddPraemieVolldeckung: "immo.differenzdeckung.praemie_volldeckung_eur",
  ddPraemieDifferenz: "immo.differenzdeckung.praemie_differenz_eur",

  // Gefahren (Versicherungssumme pro Gefahr) — both Courté- and Vorvertrags-side
  gefahrCourte: (key: string) => `immo.gefahren.courte.${key}_eur`,
  gefahrVorvertrag: (key: string) => `immo.gefahren.vorvertrag.${key}_eur`,

  // Besondere Hinweise (repeatable rows; serialized as JSON)
  besondereHinweiseJson: "immo.besondere_hinweise_json",
} as const;

export const BEDINGUNGEN_ALLGEMEIN_TEXT =
  "VSG 2022 - Stand 04/2024";

export const BEDINGUNGEN_BESONDERE_TEXT =
  "Es gilt die ‚Rahmenvereinbarung Courté für Immobilien (Gebäude)', jedoch nur so lange, wie die Betreuung über die Fa. Courté läuft, danach gilt diese nicht mehr.";
